import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { isRunningAsInstalledApp, subscribeInstalledState } from '../utils/appInstall';
import { notifyOnNewMail } from '../utils/pushNotifications';
import { LabelSidebar } from './labels';
import './Sidebar.css';

/**
 * Sidebar Component
 * Gmail-style sidebar with folders and compose button
 */
const Sidebar = ({ collapsed = false, currentUser, onNavItemClick }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = () => {
    // Close any open mail so clicking a folder always shows its list,
    // even when the route doesn't change (already on that folder).
    window.dispatchEvent(new CustomEvent('resetMailView'));
    onNavItemClick?.();
  };
  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || 'admin@atozasmail.in';
  const isAdmin = currentUser?.email?.toLowerCase() === adminEmail.toLowerCase();
  const [counts, setCounts] = useState({
    inboxUnread: 0,
    drafts: 0,
    spam: 0,
    starred: 0,
  });
  const [groups, setGroups] = useState([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [memberEmailInput, setMemberEmailInput] = useState('');
  const [memberEmails, setMemberEmails] = useState([]);
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);
  const [labelError, setLabelError] = useState('');
  const [isAppInstalled, setIsAppInstalled] = useState(isRunningAsInstalledApp);

  const [addMembersGroup, setAddMembersGroup] = useState(null);
  const [addMemberInput, setAddMemberInput] = useState('');
  const [addMemberEmails, setAddMemberEmails] = useState([]);
  const [addMemberSuggestions, setAddMemberSuggestions] = useState([]);
  const [showAddMemberSuggestions, setShowAddMemberSuggestions] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');
  const [addingMembers, setAddingMembers] = useState(false);

  const isActive = (path) => {
    return location.pathname === path;
  };

  useEffect(() => {
    return subscribeInstalledState(() => setIsAppInstalled(true));
  }, []);

  const handleDownloadApp = () => {
    window.dispatchEvent(new CustomEvent('openInstallPrompt'));
    onNavItemClick?.();
  };

  useEffect(() => {
    fetchCounts();
    fetchGroups();
    const interval = setInterval(fetchCounts, 10000);

    const handleInboxUpdate = () => {
      fetchCounts();
    };
    const handleGroupsUpdated = () => {
      fetchGroups();
    };

    window.addEventListener('inboxUpdated', handleInboxUpdate);
    window.addEventListener('groupsUpdated', handleGroupsUpdated);

    return () => {
      clearInterval(interval);
      window.removeEventListener('inboxUpdated', handleInboxUpdate);
      window.removeEventListener('groupsUpdated', handleGroupsUpdated);
    };
  }, []);

  const fetchCounts = async () => {
    try {
      const response = await axiosInstance.get('/api/mail/counts');
      if (response.data.success) {
        setCounts((prev) => ({
          ...prev,
          ...response.data.data,
        }));
        notifyOnNewMail(response.data.data, currentUser?.email);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axiosInstance.get('/api/groups');
      if (response.data.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching label groups:', error);
    }
  };

  useEffect(() => {
    const query = memberEmailInput.trim();
    if (!showLabelModal || query.length < 1) {
      setMemberSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('/api/users/suggestions', {
          params: { q: query },
        });
        if (response.data.success) {
          setMemberSuggestions(
            (response.data.data || []).filter((user) => (
              user.email !== currentUser?.email &&
              !memberEmails.includes(user.email)
            ))
          );
        }
      } catch (error) {
        console.error('Error fetching member suggestions:', error);
        setMemberSuggestions([]);
      }
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [memberEmailInput, showLabelModal, currentUser?.email, memberEmails]);

  useEffect(() => {
    const query = addMemberInput.trim();
    if (!addMembersGroup || query.length < 1) {
      setAddMemberSuggestions([]);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('/api/users/suggestions', {
          params: { q: query },
        });
        if (response.data.success) {
          const existing = new Set((addMembersGroup.members || []).map((m) => String(m.email || '').toLowerCase()));
          setAddMemberSuggestions(
            (response.data.data || []).filter((user) => (
              user.email !== currentUser?.email &&
              !addMemberEmails.includes(user.email) &&
              !existing.has(String(user.email || '').toLowerCase())
            ))
          );
        }
      } catch (error) {
        setAddMemberSuggestions([]);
      }
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [addMemberInput, addMembersGroup, currentUser?.email, addMemberEmails]);

  const handleOpenAddMembers = (event, group) => {
    event.preventDefault();
    event.stopPropagation();
    setAddMembersGroup(group);
    setAddMemberInput('');
    setAddMemberEmails([]);
    setAddMemberSuggestions([]);
    setShowAddMemberSuggestions(false);
    setAddMemberError('');
  };

  const handleCloseAddMembers = () => {
    setAddMembersGroup(null);
    setAddMemberInput('');
    setAddMemberEmails([]);
    setAddMemberSuggestions([]);
    setShowAddMemberSuggestions(false);
    setAddMemberError('');
  };

  const addAddMemberEmail = (rawEmail) => {
    const email = String(rawEmail || '').trim().toLowerCase().replace(/,$/, '');
    if (!email) return;
    if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(email)) {
      setAddMemberError('Only @atozasmail.in users can be added.');
      return;
    }
    const already = (addMembersGroup?.members || []).some(
      (m) => String(m.email || '').toLowerCase() === email
    );
    if (already) {
      setAddMemberError('That user is already a member.');
      setAddMemberInput('');
      return;
    }
    if (addMemberEmails.includes(email) || email === currentUser?.email?.toLowerCase()) {
      setAddMemberInput('');
      return;
    }
    setAddMemberEmails((prev) => [...prev, email]);
    setAddMemberInput('');
    setAddMemberSuggestions([]);
    setShowAddMemberSuggestions(false);
    setAddMemberError('');
  };

  const removeAddMemberEmail = (email) => {
    setAddMemberEmails((prev) => prev.filter((item) => item !== email));
  };

  const handleAddMemberKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addAddMemberEmail(addMemberInput);
    }
    if (event.key === 'Backspace' && !addMemberInput && addMemberEmails.length > 0) {
      setAddMemberEmails((prev) => prev.slice(0, -1));
    }
  };

  const handleSubmitAddMembers = async () => {
    if (!addMembersGroup) return;

    let members = [...addMemberEmails];
    const pending = addMemberInput.trim().toLowerCase().replace(/,$/, '');
    if (pending) {
      if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(pending)) {
        setAddMemberError('Only @atozasmail.in users can be added.');
        return;
      }
      if (pending !== currentUser?.email?.toLowerCase() && !members.includes(pending)) {
        members = [...members, pending];
      }
    }

    if (members.length === 0) {
      setAddMemberError('Add at least one member.');
      return;
    }

    setAddMemberError('');
    setAddingMembers(true);
    try {
      const response = await axiosInstance.patch(`/api/groups/${addMembersGroup._id}/members`, { members });
      if (response.data.success) {
        setGroups((prev) => prev.map((g) => (g._id === addMembersGroup._id ? response.data.data : g)));
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
        handleCloseAddMembers();
      } else {
        setAddMemberError(response.data.message || 'Could not add members');
      }
    } catch (error) {
      setAddMemberError(error.response?.data?.message || 'Could not add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleOpenLabelModal = () => {
    setNewLabelName('');
    setMemberEmailInput('');
    setMemberEmails([]);
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setLabelError('');
    setShowLabelModal(true);
  };

  const handleCloseLabelModal = () => {
    setShowLabelModal(false);
    setNewLabelName('');
    setMemberEmailInput('');
    setMemberEmails([]);
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setLabelError('');
  };

  const addMemberEmail = (rawEmail) => {
    const email = String(rawEmail || '').trim().toLowerCase().replace(/,$/, '');
    if (!email) return false;
    if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(email)) {
      setLabelError('Only @atozasmail.in users can be added to a group.');
      return false;
    }
    if (memberEmails.includes(email) || email === currentUser?.email?.toLowerCase()) {
      setMemberEmailInput('');
      return false;
    }
    setMemberEmails((prev) => [...prev, email]);
    setMemberEmailInput('');
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setLabelError('');
    return true;
  };

  const handleMemberEmailKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addMemberEmail(memberEmailInput);
    }
    if (event.key === 'Backspace' && !memberEmailInput && memberEmails.length > 0) {
      setMemberEmails((prev) => prev.slice(0, -1));
    }
  };

  const removeMemberEmail = (email) => {
    setMemberEmails((prev) => prev.filter((item) => item !== email));
  };

  const isGroupCreator = (group) =>
    String(group?.createdBy?._id || group?.createdBy) === String(currentUser?._id);

  const handleDeleteGroup = async (event, group) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isGroupCreator(group)) return;
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;

    try {
      const response = await axiosInstance.delete(`/api/groups/${group._id}`);
      if (response.data.success) {
        setGroups((prev) => prev.filter((item) => item._id !== group._id));
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
        if (location.pathname.includes(`/groups/${group._id}`)) {
          navigate('/inbox', { replace: true });
        }
      }
    } catch (error) {
      setLabelError(error.response?.data?.message || 'Could not delete group');
    }
  };

  const handleCreateLabel = async () => {
    const name = newLabelName.trim();
    if (!name) return;

    let members = [...memberEmails];
    const pending = memberEmailInput.trim().toLowerCase().replace(/,$/, '');
    if (pending) {
      if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(pending)) {
        setLabelError('Only @atozasmail.in users can be added to a group.');
        return;
      }
      if (pending !== currentUser?.email?.toLowerCase() && !members.includes(pending)) {
        members = [...members, pending];
      }
    }

    setLabelError('');

    if (members.length > 0) {
      try {
        const verifyRes = await axiosInstance.post('/api/users/verify-emails', { emails: members });
        const missing = verifyRes.data?.missing || [];
        if (missing.length > 0) {
          setLabelError(
            `Not registered in ATOZASMail: ${missing.join(', ')}. Ask them to sign up first, or remove them and create the group with only yourself.`
          );
          return;
        }
      } catch (verifyErr) {
        setLabelError(verifyErr.response?.data?.message || 'Could not verify group members');
        return;
      }
    }

    try {
      const response = await axiosInstance.post('/api/groups', {
        name,
        members,
      });
      if (response.data.success) {
        setGroups((prev) => [response.data.data, ...prev]);
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
        handleCloseLabelModal();
      }
    } catch (error) {
      setLabelError(error.response?.data?.message || 'Could not create group');
    }
  };

  return (
    <aside className={`gmail-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <Link to="/compose" className="compose-btn" title={collapsed ? "Compose" : ""} onClick={handleNavClick}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        </svg>
        {!collapsed && <span>Compose</span>}
      </Link>

      <nav className="sidebar-nav">
        <Link 
          to="/inbox" 
          className={`sidebar-item ${isActive('/inbox') ? 'active' : ''}`}
          title={collapsed ? "Inbox" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>
          {!collapsed && <span>Inbox</span>}
          {!collapsed && counts.inboxUnread > 0 && (
            <span className="sidebar-count">{counts.inboxUnread}</span>
          )}
        </Link>


        <Link 
          to="/starred" 
          className={`sidebar-item ${isActive('/starred') ? 'active' : ''}`}
          title={collapsed ? "Starred" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"/>
          </svg>
          {!collapsed && <span>Starred</span>}
        </Link>

        <Link 
          to="/snoozed" 
          className={`sidebar-item ${isActive('/snoozed') ? 'active' : ''}`}
          title={collapsed ? "Snoozed" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
          </svg>
          {!collapsed && <span>Snoozed</span>}
        </Link>

        <Link 
          to="/sent" 
          className={`sidebar-item ${isActive('/sent') ? 'active' : ''}`}
          title={collapsed ? "Sent" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
          {!collapsed && <span>Sent</span>}
        </Link>

        <Link 
          to="/drafts" 
          className={`sidebar-item ${isActive('/drafts') ? 'active' : ''}`}
          title={collapsed ? "Drafts" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
          {!collapsed && <span>Drafts</span>}
          {!collapsed && counts.drafts > 0 && (
            <span className="sidebar-count">{counts.drafts}</span>
          )}
        </Link>

        <div className="sidebar-divider"></div>

        <Link 
          to="/important" 
          className={`sidebar-item ${isActive('/important') ? 'active' : ''}`}
          title={collapsed ? "Important" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
          {!collapsed && <span>Important</span>}
        </Link>

        <Link 
          to="/scheduled" 
          className={`sidebar-item ${isActive('/scheduled') ? 'active' : ''}`}
          title={collapsed ? "Scheduled" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            <circle cx="20" cy="4" r="2"/>
          </svg>
          {!collapsed && <span>Scheduled</span>}
        </Link>

        <Link   
          to="/all-mail" 
          className={`sidebar-item ${isActive('/all-mail') ? 'active' : ''}`}
          title={collapsed ? "All Mail" : ""}
          onClick={handleNavClick}
        >

          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" opacity="0.3"/>
          </svg>
          {!collapsed && <span>All Mail</span>}
        </Link>

        <Link 
          to="/spam" 
          className={`sidebar-item ${isActive('/spam') ? 'active' : ''}`}
          title={collapsed ? "Spam" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          {!collapsed && <span>Spam</span>}
          {!collapsed && counts.spam > 0 && (
            <span className="sidebar-count">{counts.spam}</span>
          )}
        </Link>

        <Link 
          to="/trash" 
          className={`sidebar-item ${isActive('/trash') ? 'active' : ''}`}
          title={collapsed ? "Trash" : ""}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
          {!collapsed && <span>Trash</span>}
        </Link>

        {isAdmin && (
          <>
            <div className="sidebar-divider"></div>
            <Link
              to="/admin"
              className={`sidebar-item ${isActive('/admin') ? 'active' : ''}`}
              title={collapsed ? "Admin" : ""}
              onClick={handleNavClick}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l8 4v6c0 5.25-3.44 10.14-8 11.5C7.44 22.14 4 17.25 4 12V6l8-4zm0 2.18L6 7.18V12c0 4.08 2.45 7.93 6 9.32 3.55-1.39 6-5.24 6-9.32V7.18l-6-3z"/>
              </svg>
              {!collapsed && <span>Admin</span>}
            </Link>
          </>
        )}
      </nav>

      {!collapsed && (
        <div className="sidebar-workspace-sections">
          <LabelSidebar collapsed={collapsed} onNavItemClick={onNavItemClick} />

          <section className="sidebar-section">
            <div className="sidebar-section-title">
              <span>Mail Groups</span>
              <button type="button" className="sidebar-add-btn" aria-label="Add mail group" onClick={handleOpenLabelModal}>+</button>
            </div>
            {groups.map((group) => (
              <div className="sidebar-group-row" key={group._id}>
                <button
                  type="button"
                  className="sidebar-label-row sidebar-group-link"
                  onClick={() => {
                    navigate(`/groups/${group._id}`);
                    onNavItemClick?.();
                  }}
                  title={`Open ${group.name}`}
                >
                  <span className="dot red"></span>
                  <span className="sidebar-group-name">{group.name}</span>
                </button>
                {isGroupCreator(group) && (
                  <button
                    type="button"
                    className="sidebar-group-add-btn"
                    onClick={(event) => handleOpenAddMembers(event, group)}
                    aria-label={`Add members to ${group.name}`}
                    title="Add members"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-1V8H4v3H1v2h3v3h2v-3h3v-2H6zm9 3c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </button>
                )}
                {isGroupCreator(group) && (
                  <button
                    type="button"
                    className="sidebar-group-delete-btn"
                    onClick={(event) => handleDeleteGroup(event, group)}
                    aria-label={`Delete ${group.name}`}
                    title="Delete group"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </section>
        </div>
      )}
              <Link
          to="/contacts"
          className={`sidebar-item ${isActive('/contacts') ? 'active' : ''}`}
          title={collapsed ? 'Contacts' : ''}
          onClick={handleNavClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </svg>
          {!collapsed && <span>Contacts</span>}
        </Link>


      {!isAppInstalled && (
        <button
          type="button"
          className="sidebar-install-btn"
          onClick={handleDownloadApp}
          title={collapsed ? 'Download App' : ''}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
          </svg>
          {!collapsed && <span>Install App</span>}
        </button>
      )}

      {showLabelModal &&
        createPortal(
          <div className="label-modal-overlay" role="presentation" onClick={handleCloseLabelModal}>
            <div className="label-modal" role="dialog" aria-modal="true" aria-labelledby="label-modal-title" onClick={(e) => e.stopPropagation()}>
              <h2 id="label-modal-title">New Mail Group</h2>
              <label className="label-modal-field">
                <span>Please enter a new label name:</span>
                <input
                  type="text"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="label-modal-field label-member-field">
                <span>Add ATOZASMAIL users to this group (optional):</span>
                <small className="label-member-hint">
                  Pick from suggestions or press Enter. Only users who signed up in this app can be added.
                </small>
                <div className="label-member-input-wrap">
                  {memberEmails.map((email) => (
                    <span className="label-member-chip" key={email}>
                      {email}
                      <button type="button" onClick={() => removeMemberEmail(email)} aria-label={`Remove ${email}`}>x</button>
                    </span>
                  ))}
                  
                  <input
                    type="email"
                    value={memberEmailInput}
                    onChange={(e) => {
                      setMemberEmailInput(e.target.value);
                      setShowMemberSuggestions(true);
                    }}
                    onKeyDown={handleMemberEmailKeyDown}
                    onFocus={() => setShowMemberSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowMemberSuggestions(false);
                      }, 120);
                    }}
                    placeholder="user@atozasmail.in"
                    autoComplete="off"
                  />
                </div>
                {showMemberSuggestions && memberSuggestions.length > 0 && (
                  <div className="member-suggestions">
                    {memberSuggestions.map((user) => (
                      <button
                        type="button"
                        className="member-suggestion-item"
                        key={user._id || user.email}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addMemberEmail(user.email);
                        }}
                      >
                        <span className="member-suggestion-avatar">
                          {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                        <span className="member-suggestion-copy">
                          <strong>{user.username || user.email}</strong>
                          <small>{user.email}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </label>
              {labelError && <div className="label-modal-error">{labelError}</div>}
              <div className="label-modal-actions">
                <button type="button" className="label-cancel-btn" onClick={handleCloseLabelModal}>Cancel</button>
                <button type="button" className="label-create-btn" onClick={handleCreateLabel} disabled={!newLabelName.trim()}>
                  Create
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {addMembersGroup &&
        createPortal(
          <div className="label-modal-overlay" role="presentation" onClick={handleCloseAddMembers}>
            <div className="label-modal" role="dialog" aria-modal="true" aria-labelledby="add-members-title" onClick={(e) => e.stopPropagation()}>
              <h2 id="add-members-title">Add members to "{addMembersGroup.name}"</h2>
              <label className="label-modal-field label-member-field">
                <span>Add ATOZASMAIL users to this group:</span>
                <small className="label-member-hint">
                  Pick from suggestions or press Enter. Only users who signed up in this app can be added.
                </small>
                <div className="label-member-input-wrap">
                  {addMemberEmails.map((email) => (
                    <span className="label-member-chip" key={email}>
                      {email}
                      <button type="button" onClick={() => removeAddMemberEmail(email)} aria-label={`Remove ${email}`}>x</button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={addMemberInput}
                    onChange={(e) => {
                      setAddMemberInput(e.target.value);
                      setShowAddMemberSuggestions(true);
                    }}
                    onKeyDown={handleAddMemberKeyDown}
                    onFocus={() => setShowAddMemberSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowAddMemberSuggestions(false);
                      }, 120);
                    }}
                    placeholder="user@atozasmail.in"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                {showAddMemberSuggestions && addMemberSuggestions.length > 0 && (
                  <div className="member-suggestions">
                    {addMemberSuggestions.map((user) => (
                      <button
                        type="button"
                        className="member-suggestion-item"
                        key={user._id || user.email}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addAddMemberEmail(user.email);
                        }}
                      >
                        <span className="member-suggestion-avatar">
                          {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                        <span className="member-suggestion-copy">
                          <strong>{user.username || user.email}</strong>
                          <small>{user.email}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </label>
              {addMemberError && <div className="label-modal-error">{addMemberError}</div>}
              <div className="label-modal-actions">
                <button type="button" className="label-cancel-btn" onClick={handleCloseAddMembers}>Cancel</button>
                <button
                  type="button"
                  className="label-create-btn"
                  onClick={handleSubmitAddMembers}
                  disabled={addingMembers || (addMemberEmails.length === 0 && !addMemberInput.trim())}
                >
                  {addingMembers ? 'Adding...' : 'Add members'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

    </aside>
  );
};

export default Sidebar;
